<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <h2 class="title-subsection text-ink">{{ isEdit ? "Edit Campaign" : "New Campaign" }}</h2>
            <p class="body-small text-ink-muted">
                {{ isEdit ? "Update this draft before you launch it." : "Set up a new assessment campaign for your organization." }}
            </p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> Back </BaseButton>

        <BaseCard padding="lg">
            <form class="flex flex-col gap-6" @submit.prevent="submit">
                <label for="campaign-name" class="flex flex-col gap-2">
                    <span class="body-small-bold text-ink">Name</span>
                    <input
                        id="campaign-name"
                        v-model="form.name"
                        type="text"
                        required
                        maxlength="255"
                        placeholder="e.g. Q3 2026 leadership assessment"
                        class="w-full px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus body-small" />
                    <span class="details text-ink-muted">Shown to participants on their reflections page.</span>
                </label>

                <HorizontalDivider as="hr" spacing="none" />

                <fieldset class="flex flex-col gap-2">
                    <legend class="body-small-bold text-ink mb-2">Type</legend>
                    <label for="campaign-type-360" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-360" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="false" :disabled="!typeAllowed(false)" class="mt-1" />
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">360 feedback</span>
                            <span class="body-small text-ink-muted">Participants nominate raters and collect anonymous feedback.</span>
                        </span>
                    </label>
                    <label for="campaign-type-self" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-self" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="true" :disabled="!typeAllowed(true)" class="mt-1" />
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">Self reflection only</span>
                            <span class="body-small text-ink-muted">No raters — participants rate themselves and get their results right away.</span>
                        </span>
                    </label>
                </fieldset>

                <HorizontalDivider as="hr" spacing="none" />

                <label for="campaign-template" class="flex flex-col gap-2">
                    <span class="body-small-bold text-ink">Template</span>
                    <select
                        id="campaign-template"
                        v-model="form.templateId"
                        required
                        :disabled="isEdit"
                        class="w-full px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus body-small disabled:text-ink-muted">
                        <option v-for="templateOption in eligibleTemplates" :key="templateOption.id" :value="templateOption.id">{{ templateOption.name }}</option>
                    </select>
                    <span v-if="isEdit" class="details text-ink-muted">The template can't be changed after a campaign is created.</span>
                    <span v-else-if="eligibleTemplates.length === 0" class="details text-status-error">
                        No templates support this campaign type — pick a different type or add a template.
                    </span>
                    <span v-else-if="selectedTemplate" class="details text-ink-muted">
                        {{ selectedTemplate.question_count }} question{{ selectedTemplate.question_count === 1 ? "" : "s" }}
                    </span>
                </label>

                <template v-if="!form.isSelfOnly">
                    <HorizontalDivider as="hr" spacing="none" />
                    <label for="campaign-min-responses" class="flex flex-col gap-2">
                        <span class="body-small-bold text-ink">Minimum rater responses</span>
                        <input
                            id="campaign-min-responses"
                            v-model.number="form.minResponses"
                            type="number"
                            min="0"
                            max="32767"
                            required
                            class="w-32 px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus body-small" />
                        <span class="details text-ink-muted">Each participant's results unlock after this many raters respond. 0 unlocks immediately.</span>
                    </label>

                    <HorizontalDivider as="hr" spacing="none" />

                    <label for="campaign-self-assess" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-self-assess" v-model="form.selfAssessEnabled" type="checkbox" class="mt-1" />
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">Include self reflection</span>
                            <span class="body-small text-ink-muted">Participants also rate themselves, so their report shows self-vs-others gaps.</span>
                        </span>
                    </label>
                </template>

                <HorizontalDivider as="hr" spacing="none" />

                <label for="campaign-closes-at" class="flex flex-col gap-2">
                    <span class="body-small-bold text-ink">Close date <span class="body-small text-ink-muted">Optional</span></span>
                    <input
                        id="campaign-closes-at"
                        v-model="form.closesOn"
                        type="date"
                        :min="todayIso"
                        class="w-48 px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus body-small" />
                    <span class="details text-ink-muted">Campaign hard-closes at the end of this day. Leave blank for no deadline.</span>
                </label>

                <BaseNotification v-if="submitError" theme="error" :text="submitError" hide-dismiss />
            </form>
        </BaseCard>

        <div class="flex items-center justify-end gap-3 sticky bottom-4">
            <BaseButton theme="neutral" size="sm" :disabled="submitting" @click="emit('back')">Cancel</BaseButton>
            <BaseButton theme="primary" size="sm" :disabled="submitting || !canSubmit" @click="submit">{{ submitLabel }}</BaseButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, CreateCampaignRequest, TemplateSummary, UpdateCampaignRequest } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { computed, ref, watch } from "vue";

interface Props {
    templates: TemplateSummary[];
    // When set, the form edits this draft in place instead of creating one.
    campaign?: AdminCampaignSummary | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (event: "back"): void;
    (event: "created", campaign: AdminCampaignSummary): void;
    (event: "saved", campaign: AdminCampaignSummary): void;
}>();

const submitting = ref(false);
const submitError = ref<string | null>(null);
const isEdit = props.campaign != null;

// closes_at is stored as the end of the chosen day, so read the local date back.
function toLocalDateInput(iso: string | null | undefined): string {
    if (!iso) {
        return "";
    }
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const firstDefaultTemplate = props.templates.find((t) => t.assessment_mode !== "self_only") ?? props.templates[0] ?? null;
const form = ref(
    props.campaign
        ? {
              name: props.campaign.name,
              isSelfOnly: props.campaign.is_self_assessment_only,
              templateId: props.campaign.template.id,
              minResponses: props.campaign.min_responses ?? 0,
              selfAssessEnabled: props.campaign.self_assess_enabled ?? true,
              closesOn: toLocalDateInput(props.campaign.closes_at),
          }
        : {
              name: "",
              isSelfOnly: false,
              templateId: firstDefaultTemplate?.id ?? null,
              minResponses: firstDefaultTemplate?.min_responses ?? 0,
              selfAssessEnabled: true,
              closesOn: "",
          },
);

const initial = { ...form.value };

// The template is fixed while editing, so a type it doesn't support is off limits.
function typeAllowed(selfOnly: boolean): boolean {
    const mode = props.campaign?.template.assessment_mode;
    if (!mode) {
        return true;
    }
    return selfOnly ? mode !== "rater_360" : mode !== "self_only";
}

const submitLabel = computed(() => {
    if (isEdit) {
        return submitting.value ? "Saving…" : "Save changes";
    }
    return submitting.value ? "Creating…" : "Create Campaign";
});

const todayIso = new Date().toISOString().slice(0, 10);
const eligibleTemplates = computed<TemplateSummary[]>(() => {
    if (props.campaign) {
        return [props.campaign.template];
    }
    return props.templates.filter((t) => (form.value.isSelfOnly ? t.assessment_mode !== "rater_360" : t.assessment_mode !== "self_only"));
});
const selectedTemplate = computed<TemplateSummary | null>(() => eligibleTemplates.value.find((t) => t.id === form.value.templateId) ?? null);
const canSubmit = computed(() => form.value.name.trim().length > 0 && selectedTemplate.value !== null && form.value.minResponses >= 0);

watch(eligibleTemplates, (eligible) => {
    if (!eligible.some((t) => t.id === form.value.templateId)) {
        form.value.templateId = eligible[0]?.id ?? null;
    }
});

watch(selectedTemplate, (template) => {
    if (template) {
        form.value.minResponses = template.min_responses;
    }
});

// A self-only draft carries a zero minimum; coming back to 360 should not
// keep it, or participants could unlock results with no raters. A minimum
// the admin set while the type was 360 survives a round trip through
// self-only, including a deliberate zero.
let lastMinResponsesFor360: number | null = form.value.isSelfOnly ? null : form.value.minResponses;
watch(
    () => form.value.isSelfOnly,
    (selfOnly) => {
        if (selfOnly) {
            lastMinResponsesFor360 = form.value.minResponses;
            return;
        }
        if (lastMinResponsesFor360 !== null) {
            form.value.minResponses = lastMinResponsesFor360;
        } else if (form.value.minResponses === 0 && selectedTemplate.value) {
            form.value.minResponses = selectedTemplate.value.min_responses;
        }
    },
);

function payload(): Omit<CreateCampaignRequest, "template_id"> {
    return {
        name: form.value.name.trim(),
        is_self_assessment_only: form.value.isSelfOnly,
        min_responses: form.value.isSelfOnly ? 0 : form.value.minResponses,
        self_assess_enabled: form.value.isSelfOnly ? true : form.value.selfAssessEnabled,
        closes_at: form.value.closesOn ? new Date(`${form.value.closesOn}T23:59:59`).toISOString() : null,
    };
}

// Only what the admin changed goes out, so a save from an older form can't
// overwrite another admin's edit, and an untouched close date is not
// rebuilt in this browser's timezone. The type governs the rater fields, so
// a type change sends those too.
function patchBody(): UpdateCampaignRequest {
    const full = payload();
    const body: UpdateCampaignRequest = {};
    const typeChanged = form.value.isSelfOnly !== initial.isSelfOnly;
    if (form.value.name.trim() !== initial.name.trim()) {
        body.name = full.name;
    }
    if (typeChanged) {
        body.is_self_assessment_only = full.is_self_assessment_only;
    }
    if (typeChanged || form.value.minResponses !== initial.minResponses) {
        body.min_responses = full.min_responses;
    }
    if (typeChanged || form.value.selfAssessEnabled !== initial.selfAssessEnabled) {
        body.self_assess_enabled = full.self_assess_enabled;
    }
    if (form.value.closesOn !== initial.closesOn) {
        body.closes_at = full.closes_at;
    }
    return body;
}

async function submit() {
    if (!canSubmit.value) {
        return;
    }
    submitting.value = true;
    submitError.value = null;
    try {
        const request = props.campaign
            ? useFetch(`/v1/reflections/admin/campaigns/${props.campaign.id}`).patch(JSON.stringify(patchBody()), "application/json")
            : useFetch("/v1/reflections/admin/campaigns").post(JSON.stringify({ ...payload(), template_id: form.value.templateId }), "application/json");
        const { data, error: fetchError } = await request.json<AdminCampaignResponse>();
        if (fetchError.value) {
            submitError.value =
                (data.value as { error?: string } | null)?.error ||
                (isEdit ? "Could not save the campaign. Please try again." : "Could not create the campaign. Please try again.");
            return;
        }
        if (data.value && isEdit) {
            emit("saved", data.value.campaign);
        } else if (data.value) {
            emit("created", data.value.campaign);
        }
    } finally {
        submitting.value = false;
    }
}
</script>
